import { useCompany, useGetTimeZones } from '@dispatch/shared/hooks';
import {
  AssignmentCategoryOptions,
  AttributeOptions,
  FNOL_RequestForm,
  FNOL_TaskTypeOptions,
  FNOL_TaskTypes,
  ResultStatus,
  SeverityOptions,
} from '@dispatch/shared/types';
import { useCurrentUser } from '@dispatch/web/auth';
import {
  DateTimeField,
  Section,
  SelectField,
  SubSection,
  TextField,
} from '@dispatch/web/ui';
import { Divider, Grid, InputAdornment } from '@mui/material';
import {
  Control,
  FieldErrors,
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { FNOLClaimAttributesSection } from './FNOLClaimAttributes/FNOLClaimAttributesSection';
import { useEffect, useMemo } from 'react';
import { useGeneralDataContext } from '@dispatch/shared/hooks';

export type QualifiedFNOLRequestSectionProps = {
  control: Control<FieldValues>;
  errors: FieldErrors;
  companyAttributes: AttributeOptions[];
  pinnedAttributeGuids: readonly string[] | undefined;
  watch: UseFormWatch<FNOL_RequestForm>;
  getValues: UseFormGetValues<FNOL_RequestForm>;
  setValue: UseFormSetValue<FNOL_RequestForm>;
  onClaimNumberChange: (claimNumber: string) => void;
  cwsStatus?: ResultStatus;
  isDolLocked: boolean;
};

export const QualifiedFNOLRequestSection = ({
  control,
  errors,
  companyAttributes,
  pinnedAttributeGuids,
  watch,
  getValues,
  setValue,
  onClaimNumberChange,
  cwsStatus,
  isDolLocked,
}: QualifiedFNOLRequestSectionProps) => {
  const { tenantId } = useCurrentUser();
  const { featureFlags } = useCompany();
  const { statesOptions, methodOfInspectionOptions } = useGeneralDataContext();
  const { perilOptions } = useCompany();
  const { timeZones } = useGetTimeZones(tenantId || '');
  const isNonComplex = watch('fnolTaskType') === FNOL_TaskTypes.NON_COMPLEX;
  const isComplex = watch('fnolTaskType') === FNOL_TaskTypes.COMPLEX;

  useEffect(() => {
    if (!isNonComplex) {
      setValue('durationInMinutes', 0);
    } else {
      setValue('durationInMinutes', 90);
    }
  }, [isNonComplex, setValue]);

  const preferredLanguages = useMemo(
    () =>
      companyAttributes
        .find((attribute) => attribute.title === 'Languages Spoken')
        ?.attributeDefinitionValues.map((value) => ({
          title: value.title,
          value: value.value,
        })),
    [companyAttributes]
  );

  const renderClaimAttributes = () => {
    return (
      <FNOLClaimAttributesSection
        control={control}
        watch={watch}
        getValues={getValues}
        companyAttributes={companyAttributes}
        pinnedAttributeGuids={pinnedAttributeGuids}
      />
    );
  };

  return (
    <Section title="FNOL Request">
      <Grid container spacing={2} alignItems="center">
        <Grid size={6}>
          <TextField
            id="claimNumber"
            name="claimNumber"
            label="Claim Number"
            control={control}
            errors={errors}
            inputProps={{
              endAdornment:
                cwsStatus === 'loading' ? (
                  <InputAdornment position="end">
                    Fetching data...
                  </InputAdornment>
                ) : undefined,
            }}
            onChange={(event) => onClaimNumberChange(event.target.value)}
            data-testid="counties-field"
          />
        </Grid>
      </Grid>
      <SubSection title={'TimeZone'}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={6}>
            <SelectField
              id="timeZone"
              name="timeZone"
              label="Time Zone"
              control={control}
              errors={errors}
              options={timeZones || []}
              required={isNonComplex || !isDolLocked}
            />
          </Grid>
        </Grid>
      </SubSection>
      <SubSection title="Location Information">
        <Grid container alignItems="center" spacing={2}>
          <Grid size={12}>
            <TextField
              id="streetLine1"
              name="lossLocation.streetLine1"
              label="Street Line 1"
              control={control}
              errors={errors['lossLocation']}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              id="streetLine2"
              name="lossLocation.streetLine2"
              label="Street Line 2"
              control={control}
              errors={errors['lossLocation']}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              id="city"
              name="lossLocation.city"
              label="City"
              control={control}
              errors={errors['lossLocation']}
              required
            />
          </Grid>
          <Grid size={6}>
            <SelectField
              id="state"
              name="lossLocation.state"
              label="State"
              control={control}
              errors={errors['lossLocation']}
              options={statesOptions}
              required
              autoComplete="new-password"
            />
          </Grid>
          <Grid size={6}>
            <TextField
              id="zip"
              name="lossLocation.zip"
              label="Zip"
              control={control}
              errors={errors['lossLocation']}
            />
          </Grid>
        </Grid>
      </SubSection>
      <Divider style={{ marginTop: 20, marginBottom: 20 }} />
      <SubSection title="Information">
        <Grid container alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Grid size={3}>
            <DateTimeField
              id="startTime"
              name="startTime"
              label="Start Date Time"
              control={control}
              errors={errors}
              required={isNonComplex}
            />
          </Grid>
          <Grid size={3}>
            <DateTimeField
              id="endTime"
              name="endTime"
              label="End Date Time"
              control={control}
              errors={errors}
              required={isNonComplex}
            />
          </Grid>
          <Grid size={3}>
            <DateTimeField
              id="dateOfLoss"
              name="dateOfLoss"
              label="Date of Loss"
              control={control}
              errors={errors}
              disabled={isDolLocked}
              required={!isDolLocked}
            />
          </Grid>
        </Grid>
        <Grid container alignItems="center" spacing={2}>
          <Grid size={3}>
            {featureFlags.qualifiedResourcesFnol ? (
              <TextField
                id="fnolTaskType"
                name="fnolTaskType"
                label="Task Type"
                control={control}
                errors={errors}
                disabled
                defaultValue={'Claim Owner'}
                required
              />
            ) : (
              <SelectField
                id="fnolTaskType"
                name="fnolTaskType"
                label="Task Type"
                control={control}
                errors={errors}
                options={FNOL_TaskTypeOptions}
                required
              />
            )}
          </Grid>
          <Grid size={3}>
            {featureFlags.qualifiedResourcesFnol ? (
              <TextField
                id="methodOfInspection"
                name="methodOfInspection"
                label="Method of Inspection"
                defaultValue={'FIELD'}
                control={control}
                errors={errors}
                disabled
                required={!isComplex}
              />
            ) : (
              <SelectField
                id="methodOfInspection"
                name="methodOfInspection"
                label="Method of Inspection"
                control={control}
                errors={errors}
                options={methodOfInspectionOptions || []}
                required={!isComplex}
              />
            )}
          </Grid>
          <Grid size={3}>
            <SelectField
              id="assignmentCategory"
              name="assignmentCategory"
              label="Assignment Category"
              control={control}
              errors={errors}
              options={AssignmentCategoryOptions}
            />
          </Grid>
          <Grid size={3}>
            <TextField
              id="catCode"
              name="catCode"
              label="CAT Code"
              control={control}
              errors={errors}
              inputLabelProps={{ shrink: !!watch('catCode') }}
            />
          </Grid>
          <Grid size={2}>
            <TextField
              id="durationInMinutes"
              name="durationInMinutes"
              label="Duration in Minutes"
              type={'number'}
              control={control}
              errors={errors}
              required={isNonComplex}
            />
          </Grid>
          <Grid size={3}>
            <SelectField
              id="severity"
              name="severity"
              label="Severity"
              control={control}
              errors={errors}
              options={SeverityOptions}
            />
          </Grid>
          <Grid size={3}>
            <SelectField
              id="peril"
              name="peril"
              label="Peril"
              control={control}
              errors={errors}
              options={perilOptions || []}
              required
            />
          </Grid>
          <Grid size={3}>
            <SelectField
              id="preferredLanguage"
              name="preferredLanguage"
              label="Preferred Language"
              control={control}
              errors={errors}
              options={preferredLanguages || []}
            />
          </Grid>
        </Grid>
      </SubSection>
      <Divider style={{ marginTop: 20, marginBottom: 20 }} />
      {renderClaimAttributes()}
    </Section>
  );
};
